/**
 * Locale-aware date and number formatting helpers (I18N-10/11).
 *
 * These wrappers accept the app's resolved locale string (from `useLocale`)
 * rather than letting the browser pick its own default — ensuring dates and
 * numbers render in the language the user explicitly chose, not the browser's
 * OS locale.
 *
 * Usage:
 *   const { locale } = useLocale();
 *   fmtDate("2024-03-15", locale)                  // "15/03/2024" in he
 *   fmtDateTime(new Date(), locale)                // full date + time string
 *   fmtTime("2024-03-15T14:30:00Z", locale, ...)  // "14:30" with options
 *   fmtNumber(12345, locale)                       // "12,345" in en / "12 345" in ru
 */

/** Format a date (no time) using the given locale. */
export function fmtDate(
  date: Date | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  try {
    return d.toLocaleDateString(locale, options);
  } catch {
    return d.toLocaleDateString(undefined, options);
  }
}

/** Format a date + time using the given locale. */
export function fmtDateTime(
  date: Date | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  try {
    return d.toLocaleString(locale, options);
  } catch {
    return d.toLocaleString(undefined, options);
  }
}

/** Format a time-only value using the given locale. */
export function fmtTime(
  date: Date | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  try {
    return d.toLocaleTimeString(locale, options);
  } catch {
    return d.toLocaleTimeString(undefined, options);
  }
}

/** Format a number using the given locale (thousands separators, etc.). */
export function fmtNumber(
  n: number,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  try {
    return n.toLocaleString(locale, options);
  } catch {
    return n.toLocaleString(undefined, options);
  }
}
